import { apiClient } from '../api.client';
import {
  Booking,
  BookingListResponse,
  BookingResponse,
  BookingStatistics,
  BookingStatus,
  BookingPaymentStatus,
} from './types';

export interface ListBookingsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: BookingStatus;
  paymentStatus?: BookingPaymentStatus;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  sort_by?: string;
  sort_dir?: 'ASC' | 'DESC';
}

export async function getBookings(params: ListBookingsParams = {}): Promise<BookingListResponse> {
  const queryParams: Record<string, any> = {
    page: params.page || 1,
    limit: params.limit || 20,
    sort_by: params.sort_by || 'created_at',
    sort_dir: params.sort_dir || 'DESC',
  };

  if (params.search) queryParams.search = params.search;
  if (params.status) queryParams.status = params.status;
  if (params.paymentStatus) queryParams.paymentStatus = params.paymentStatus;
  if (params.customerId) queryParams.customerId = params.customerId;
  if (params.startDate) queryParams.startDate = params.startDate;
  if (params.endDate) queryParams.endDate = params.endDate;

  const response = await apiClient.get<Booking[]>(`/api/bookings`, queryParams);
  return response as unknown as BookingListResponse;
}

export async function getBookingById(bookingId: string): Promise<BookingResponse> {
  const response: any = await apiClient.get<any>(`/api/bookings/${bookingId}`);
  // API returns booking directly, not wrapped in { data }
  // Check if response.data exists and booking fields are in data
  if (response && response.data && response.data.id) {
    return { status: 'success', code: 200, message: 'OK', data: response.data };
  }
  // If response itself is the booking (direct return)
  if (response && response.id) {
    return { status: 'success', code: 200, message: 'OK', data: response };
  }
  return response as BookingResponse;
}

export async function createBooking(booking: Partial<Booking>): Promise<BookingResponse> {
  const response = await apiClient.post<Booking>('/api/bookings', booking);
  return response as unknown as BookingResponse;
}

export async function updateBooking(
  bookingId: string,
  booking: Partial<Booking>
): Promise<BookingResponse> {
  const response = await apiClient.put<Booking>(`/api/bookings/${bookingId}`, booking);
  return response as unknown as BookingResponse;
}

export async function updateBookingStatus(
  bookingId: string,
  status: BookingStatus,
  reason?: string
): Promise<BookingResponse> {
  const response = await apiClient.patch<Booking>(`/api/bookings/${bookingId}/status`, {
    status,
    reason,
  });
  return response as unknown as BookingResponse;
}

export async function deleteBooking(bookingId: string): Promise<void> {
  await apiClient.delete(`/api/bookings/${bookingId}`);
}

export async function deleteBookingsBulk(bookingIds: string[]): Promise<void> {
  await apiClient.post('/api/bookings/bulk', { ids: bookingIds });
}

export async function updateBookingsStatusBulk(
  bookingIds: string[],
  status: BookingStatus,
): Promise<void> {
  await apiClient.patch('/api/bookings/bulk/status', { ids: bookingIds, status });
}

export async function getBookingsByCustomer(customerId: string): Promise<BookingResponse> {
  const response = await apiClient.get<Booking[]>(`/api/bookings/customer/${customerId}`);
  return response as unknown as BookingResponse;
}

export async function getBookingStatistics(
  startDate?: string,
  endDate?: string
): Promise<{ data: BookingStatistics }> {
  const params: Record<string, any> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await apiClient.get<BookingStatistics>('/api/bookings/statistics', params);
  return response as unknown as { data: BookingStatistics };
}

export async function markBookingPaid(bookingId: string): Promise<BookingResponse> {
  return updateBookingStatus(bookingId, BookingStatus.CONFIRMED);
}

export async function confirmBooking(bookingId: string, notes?: string): Promise<BookingResponse> {
  const response = await apiClient.post<Booking>(`/api/bookings/${bookingId}/confirm`, { notes });
  return response as unknown as BookingResponse;
}

export async function cancelBooking(bookingId: string, reason?: string): Promise<BookingResponse> {
  return updateBookingStatus(bookingId, BookingStatus.CANCELLED, reason);
}

export async function refundBooking(bookingId: string): Promise<BookingResponse> {
  return updateBookingPaymentStatus(bookingId, BookingPaymentStatus.REFUNDED);
}

export interface ProcessPaymentParams {
  paymentMethod: string;
  amount?: number;
  reference?: string;
  notes?: string;
}

export async function processBookingPayment(
  bookingId: string,
  params: ProcessPaymentParams
): Promise<BookingResponse> {
  const response = await apiClient.post<Booking>(`/api/bookings/${bookingId}/payment`, params);
  return response as unknown as BookingResponse;
}

export async function updateBookingPaymentStatus(
  bookingId: string,
  paymentStatus: BookingPaymentStatus,
): Promise<BookingResponse> {
  const response = await apiClient.patch<Booking>(`/api/bookings/${bookingId}/payment-status`, {
    paymentStatus,
  });
  return response as unknown as BookingResponse;
}
